import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
// Author      : Dirk Rothe, Tobias Schwarzkopf
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 161511 $
// Purpose     : login/logout parts
// =============================================================================
// ---Third-party imports
import React, { useState, useEffect } from 'react';
import { Icon, Header, Form, Segment, Button, Input, Message, Image } from 'semantic-ui-react';
// ---Framework imports
import { get } from 'ldash';
import { Actions } from 'uix/registries';
import log from 'uix/logging';
import { usePersistent, useActions, useSelector, useCallApi, useDebounce } from 'uix/hooks';
import { HeaderSettingsIcon } from 'uix/components';
import { WebAuthNDiag } from './components';
import connect from 'uix/connect';
import { queryWebauthn, setWebauthn, clearLoginFailure, runLogin } from './actions';
import { VIcon, vicon } from 'uix/graphics';
import { Sel } from 'uix/registries';
// ---Local imports
import styles from './auth.css';
import clsx from 'clsx';
// ---Exports
const RecoverForm = ({ username, onClose })=>{
    let i18n = useSelector(Sel.i18n);
    let { callApi: recoverPasswordMail } = useCallApi('user/recoverPasswordMail');
    let [email, setEmail] = useState('');
    let [recoverFailure, setRecoverFailure] = useState('');
    let [mailSuccess, setMailSuccess] = useState(false);
    let sendMail = ()=>{
        setRecoverFailure('');
        recoverPasswordMail({
            login: username,
            email
        }, (data, error)=>{
            setMailSuccess(!!data?.mail_send);
            if (data?.recover_error) {
                setRecoverFailure(data?.recover_error);
            }
        });
    };
    let isValidEmail = (email)=>{
        let emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };
    return /*#__PURE__*/ _jsxs(Form, {
        className: clsx('attached fluid segment', styles.formContainer),
        onSubmit: ()=>sendMail(),
        error: !!recoverFailure,
        success: mailSuccess,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: styles.headerForget,
                style: {},
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        secondary: true,
                        icon: "arrow left",
                        onClick: onClose
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        children: i18n.get('resetpw')
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                style: {
                    margin: '1rem 0 ',
                    textAlign: 'center'
                },
                children: [
                    "Geben Sie die mit ",
                    /*#__PURE__*/ _jsx("b", {
                        children: username
                    }),
                    " verbundene E-Mail Adresse ein:"
                ]
            }),
            /*#__PURE__*/ _jsxs(Input, {
                name: "email",
                value: email,
                iconPosition: "left",
                placeholder: i18n.get('email', 'Email'),
                onChange: (e, { value })=>setEmail(value),
                required: true,
                fluid: true,
                ref: (input)=>input ? input.focus() : null,
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: "at"
                    }),
                    /*#__PURE__*/ _jsx("input", {})
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.buttonArea,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs(Message, {
                                size: "mini",
                                error: true,
                                children: [
                                    /*#__PURE__*/ _jsx(Icon, {
                                        name: "exclamation"
                                    }),
                                    recoverFailure
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Message, {
                                size: "mini",
                                success: true,
                                children: "Success"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        disabled: !isValidEmail(email),
                        className: styles.submit,
                        fluid: true,
                        content: "Neues Passwort anfordern"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {})
        ]
    });
};
export const AuthLogin = ()=>{
    let [doRunLogin, setWebauthnState, clearFailure, doQueryWebauthn] = useActions([
        runLogin,
        setWebauthn,
        clearLoginFailure,
        queryWebauthn
    ]);
    let apiurl = useSelector(Sel.apiurl);
    let url = apiurl.href(`/favicon.ico`);
    let urlStr = `${apiurl.baseUrl('').slice(8)}`;
    let [username, setUsername] = usePersistent('username', '');
    let [debouncedUsername] = useDebounce(username, 350);
    let [showPassword, setShowPassword] = usePersistent('showPassword', true);
    let [password, setPassword] = useState('');
    let email_on = useSelector((root)=>root.config.instance.email.on);
    let { loginFailure, user, i18n, request, webauthn } = useSelector((root)=>root.meta);
    let webauthnForCurrentUser = webauthn.available_for === username;
    let webauthnAvailable = webauthnForCurrentUser && webauthn.available && webauthn.available !== 'LOADING';
    let webauthnError = webauthnForCurrentUser && webauthn.state && webauthn.state.startsWith('ERR_');
    let usePassword = showPassword || webauthnError;
    let noInsufficientPermissionHack = request.classname == 'test' && request.tabname == 'auth';
    let failure = null;
    // show recover form instead of login form
    let [recoverMode, setRecoverMode] = useState(false);
    let onPasswordRecoverClick = null;
    if (email_on) {
        onPasswordRecoverClick = ()=>setRecoverMode(true);
    }
    if (loginFailure) {
        let reason = loginFailure.reason;
        let isWebauthnCancelled = reason == 'WBA_LOGIN_CANCEL';
        let isWebauthnSetupFailure = reason && reason.startsWith('WBA_SETUP_');
        if (isWebauthnCancelled) {
            reason = get(i18n, 'msg_webauthn_login_cancelled', 'Passkey login was cancelled. Retry or use password.');
        } else if (isWebauthnSetupFailure) {
            if (reason == 'WBA_SETUP_ERR_REG_CREATE' || reason == 'WBA_SETUP_ERR_REG_CREATE_CANCEL') {
                reason = get(i18n, 'msg_webauthn_setup_cancelled', 'Passkey setup was cancelled. You are not logged in.');
            } else if (reason == 'WBA_SETUP_ERR_REG_CREATE_EXISTS') {
                reason = get(i18n, 'msg_webauthn_setup_exists', 'A passkey already exists on this device for this account.');
            } else if (reason == 'WBA_SETUP_ERR_REG_CREATE_SEC' || reason == 'WBA_SETUP_ERR_REG_CREATE_UNSUP') {
                reason = get(i18n, 'msg_webauthn_setup_unsupported', 'Passkey setup is not available in this browser or context.');
            } else {
                reason = get(i18n, 'msg_webauthn_setup_failed', 'Passkey setup failed. You are not logged in.');
            }
        } else if (reason == 'WBA_ERR_LOGIN_CREATE_EXISTS' || reason == 'WBA_ERR_LOGIN_CREATE_SEC' || reason == 'WBA_ERR_LOGIN_CREATE_UNSUP') {
            reason = get(i18n, 'msg_webauthn_login_failed', 'Passkey login failed. Retry or use password.');
        } else {
            reason = get(i18n, reason, reason);
        }
        failure = /*#__PURE__*/ _jsxs(Message, {
            className: styles.failure,
            size: "mini",
            error: !isWebauthnCancelled && !isWebauthnSetupFailure,
            warning: isWebauthnCancelled || isWebauthnSetupFailure,
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "exclamation"
                }),
                reason
            ]
        });
    } else if (user.name && !noInsufficientPermissionHack) {
        // if we have a valid login, assume it's a permission problem
        // we also need to handle i18n manually
        failure = /*#__PURE__*/ _jsxs(Message, {
            className: styles.failure,
            size: "mini",
            error: true,
            children: [
                /*#__PURE__*/ _jsx(Icon, {
                    name: "exclamation"
                }),
                i18n['msg_insufficient_permission']
            ]
        });
    }
    useEffect(()=>{
        if (!debouncedUsername) {
            setWebauthnState({
                available: null,
                available_for: null
            });
            return;
        }
        doQueryWebauthn(debouncedUsername);
    }, [
        debouncedUsername
    ]);
    useEffect(()=>{
        if (webauthnForCurrentUser && webauthn.available === false && !showPassword) {
            setShowPassword(true);
        }
    }, [
        webauthnForCurrentUser,
        webauthn.available,
        showPassword
    ]);
    useEffect(()=>{
        if (webauthn.state == 'REG_OK') {
            setShowPassword(false);
        }
    }, [
        webauthn.state
    ]);
    let runLoginHandler = ()=>doRunLogin({
            username: username,
            password: usePassword ? password : null,
            setupPasskey: false
        });
    let runPasskeySetupHandler = ()=>{
        clearFailure();
        doRunLogin({
            username: username,
            password,
            setupPasskey: true
        });
    };
    let passkeyNotInitialized = webauthnForCurrentUser && webauthn.available === false;
    let showPasskeySetup = usePassword && passkeyNotInitialized;
    let disablePasskeySetup = !Boolean(username && password);
    let content = /*#__PURE__*/ _jsxs(Form, {
        className: clsx('attached fluid segment', styles.formContainer),
        onSubmit: ()=>runLoginHandler(),
        children: [
            /*#__PURE__*/ _jsx(Image, {
                src: url,
                onError: (e)=>{
                    e.target.onError = null; //prevents looping
                    e.target.src = ''; //fallback image
                },
                style: {
                    height: '3rem'
                }
            }),
            /*#__PURE__*/ _jsxs(Header, {
                className: styles.header,
                textAlign: "center",
                as: "h2",
                children: [
                    get(i18n, 'welcomeBack', 'Welcome back'),
                    "!",
                    /*#__PURE__*/ _jsx(Header.Subheader, {
                        size: "tiny",
                        children: urlStr
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Field, {
                className: styles.inputs,
                children: /*#__PURE__*/ _jsxs(Input, {
                    name: "username",
                    autoComplete: "username",
                    value: username,
                    iconPosition: "left",
                    placeholder: get(i18n, 'username', 'User'),
                    onChange: (e, { value })=>setUsername(value),
                    required: true,
                    ref: (input)=>input && (!username || !usePassword) ? input.focus() : null,
                    children: [
                        /*#__PURE__*/ _jsx(VIcon, {
                            name: "user"
                        }),
                        /*#__PURE__*/ _jsx("input", {})
                    ]
                })
            }),
            usePassword ? /*#__PURE__*/ _jsx(PasswordField, {
                isFocused: Boolean(username),
                placeholder: get(i18n, 'password', 'Password'),
                onChange: (password)=>setPassword(password),
                showAlways: showPassword || webauthnError,
                webauthnAvailable: webauthnAvailable,
                onPasswordRecoverClick: onPasswordRecoverClick
            }) : null,
            !showPassword || webauthnAvailable ? /*#__PURE__*/ _jsxs(Button.Group, {
                fluid: true,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        icon: "keyboard outline",
                        style: {
                            paddingRight: '1em'
                        },
                        positive: usePassword,
                        title: "Use Password",
                        onClick: ()=>{
                            clearFailure();
                            setShowPassword(true);
                        }
                    }),
                    /*#__PURE__*/ _jsx(Button.Or, {}),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        positive: !usePassword,
                        title: "Use Passkey",
                        icon: {
                            ...vicon('fingerprint'),
                            style: {
                                paddingLeft: '0.3em'
                            }
                        },
                        onClick: ()=>{
                            clearFailure();
                            setPassword('');
                            setWebauthnState({
                                state: null,
                                error: null
                            });
                            setShowPassword(false);
                            if (username && webauthnAvailable === true) {
                                doRunLogin({
                                    username: username,
                                    password: null,
                                    setupPasskey: false
                                });
                            }
                        }
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsxs("div", {
                className: clsx(styles.buttonArea, failure && styles.failure),
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: failure
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: styles.submitRow,
                            children: [
                                /*#__PURE__*/ _jsxs(Button, {
                                    className: styles.submit,
                                    fluid: true,
                                    animated: "fade",
                                    children: [
                                        /*#__PURE__*/ _jsx(Button.Content, {
                                            visible: true,
                                            children: get(i18n, 'doLoginButtonLabel', 'Log in')
                                        }),
                                        /*#__PURE__*/ _jsx(Button.Content, {
                                            hidden: true,
                                            children: /*#__PURE__*/ _jsx(VIcon, {
                                                name: "sign-in"
                                            })
                                        })
                                    ]
                                }),
                                showPasskeySetup ? /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    basic: true,
                                    icon: {
                                        ...vicon('fingerprint')
                                    },
                                    title: get(i18n, 'setupPasskeyButtonLabel', 'Set up passkey'),
                                    disabled: disablePasskeySetup,
                                    onClick: runPasskeySetupHandler
                                }) : null
                            ]
                        })
                    })
                ]
            })
        ]
    });
    if (recoverMode) {
        content = /*#__PURE__*/ _jsx(RecoverForm, {
            username: username,
            onClose: ()=>setRecoverMode(false)
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: styles.container,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.authContainer,
            children: content
        })
    });
};
let PasswordField = connect('LoginPasswordField', {}, ({ isFocused, placeholder, onChange, showAlways, webauthnAvailable, onPasswordRecoverClick })=>{
    let [password, setPassword] = useState('');
    let i18n = useSelector(Sel.i18n);
    let the_input = null;
    useEffect(()=>{
        if (isFocused && the_input) {
            the_input.focus();
        }
    }, [
        showAlways
    ]);
    let change = (value)=>{
        onChange(value);
        setPassword(value);
    };
    if (!showAlways && webauthnAvailable !== true) {
        return null;
    }
    return /*#__PURE__*/ _jsxs(Form.Field, {
        style: {
            position: 'relative'
        },
        children: [
            /*#__PURE__*/ _jsxs(Input, {
                name: "password",
                autoComplete: "current-password",
                value: password,
                type: "password",
                iconPosition: "left",
                placeholder: placeholder,
                onChange: (e, { value })=>change(value),
                required: true,
                ref: (input)=>the_input = input,
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: "lock"
                    }),
                    /*#__PURE__*/ _jsx("input", {})
                ]
            }),
            onPasswordRecoverClick && /*#__PURE__*/ _jsxs("a", {
                style: {
                    cursor: 'pointer',
                    paddingTop: '.3em',
                    textAlign: 'right',
                    width: '100%',
                    display: 'block',
                    fontSize: '.85em'
                },
                onClick: onPasswordRecoverClick,
                children: [
                    i18n.get('resetpw'),
                    " ?"
                ]
            })
        ]
    });
});
export const WebAuthN = ({})=>/*#__PURE__*/ _jsxs(Segment, {
        children: [
            /*#__PURE__*/ _jsx(HeaderSettingsIcon, {
                content: "WebAuthN",
                subheader: "test registration and login procedure",
                icon: vicon('fingerprint')
            }),
            /*#__PURE__*/ _jsx(WebAuthNDiag, {})
        ]
    });
